/**
 * Phase L.1.1 — Single source of truth for the night-mode clock
 * boundaries + the auto-dark resolver.
 *
 * Consumed by:
 *   1. `theme/ThemeProvider.jsx`          — React state resolver (runtime).
 *   2. `public/index.html` inline script  — pre-hydration FOWT
 *                                           prevention (MUST hardcode the
 *                                           same integer literals — the
 *                                           Family L.1.1 parity test
 *                                           enforces this).
 *
 * If you change either constant here, update the inline script's
 * literal in `public/index.html` in the same commit. The parity test
 * (`nightModeBoundary.test.jsx::L1.1.4`) will fail until they match —
 * that's the intended safety net.
 *
 * The window follows local clock time:
 *   - `[NIGHT_START_HOUR, 24)` UNION `[0, NIGHT_END_HOUR)` → dark
 *   - `[NIGHT_END_HOUR, NIGHT_START_HOUR)` → light
 * so 7pm-7am is dark, 7am-7pm is light, in the visitor's local time.
 */
export const NIGHT_START_HOUR = 19; // 7pm — dark begins at :00
export const NIGHT_END_HOUR = 7;    // 7am — light resumes at :00

export function isAutoDarkAt(date) {
  const h = date.getHours();
  return h >= NIGHT_START_HOUR || h < NIGHT_END_HOUR;
}

/**
 * Milliseconds until the next NIGHT_START_HOUR or NIGHT_END_HOUR
 * crossing from `date` (in the visitor's local timezone).
 * Exported so ThemeProvider can schedule a re-eval at the crossing.
 */
export function msUntilNextCrossing(date) {
  const now = date.getTime();
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  const candidates = [
    new Date(y, m, d, NIGHT_END_HOUR, 0, 0, 0).getTime(),       // today 7am
    new Date(y, m, d, NIGHT_START_HOUR, 0, 0, 0).getTime(),     // today 7pm
    new Date(y, m, d + 1, NIGHT_END_HOUR, 0, 0, 0).getTime(),   // tomorrow 7am
    new Date(y, m, d + 1, NIGHT_START_HOUR, 0, 0, 0).getTime(), // tomorrow 7pm
  ].filter((t) => t > now);
  return Math.min(...candidates) - now;
}
